import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Coins, Zap } from "lucide-react";
import { toast } from "sonner";

export default function Budgets() {
  const [agents, setAgents] = useState([]);
  const [budgets, setBudgets] = useState({});
  const [defaultCap, setDefaultCap] = useState(5);
  const [drafts, setDrafts] = useState({});
  const [cost, setCost] = useState(null);

  const load = async () => {
    const [ag, bg, co] = await Promise.all([
      api.get("/agents"),
      api.get("/budgets"),
      api.get("/cost/overview"),
    ]);
    setAgents(ag.data.agents.filter((a) => a.status === "active"));
    setBudgets(bg.data.budgets);
    setDefaultCap(bg.data.default_daily_usd);
    setCost(co.data);
  };
  useEffect(() => { load(); }, []);

  const save = async (id) => {
    const val = parseFloat(drafts[id]);
    if (isNaN(val) || val < 0) return toast.error("Valor inválido");
    try {
      await api.post("/budgets", { agent_id: id, daily_usd: val });
      toast.success(`${id.toUpperCase()} → $${val}/día`);
      setDrafts({ ...drafts, [id]: undefined });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fallo");
    }
  };

  const spentFor = (id) => {
    if (!cost) return 0;
    const rows = cost.per_agent.filter((p) => p.agent_id === id);
    return rows.reduce((s, r) => s + r.cost_usd, 0);
  };

  return (
    <div className="p-8 space-y-6" data-testid="budgets-page">
      <div className="border-b border-[var(--border)] pb-5">
        <div className="label-mono mb-2">AI COST CONTROL · PRESUPUESTOS POR AGENTE</div>
        <h1 className="font-display font-black text-4xl tracking-tight">Presupuestos</h1>
        <p className="text-sm text-[var(--text-dim)] mt-1">
          Cada agente tiene un tope diario en USD. Al superarlo, el chat se bloquea con 402.
          Default: <span className="font-mono text-[var(--accent)]">${defaultCap}/día</span>.
        </p>
      </div>

      <div className="border border-[var(--border)] bg-[var(--surface)]">
        <div className="grid grid-cols-[1fr_140px_140px_180px_120px] label-mono py-3 px-4 border-b border-[var(--border)] bg-[var(--surface-2)]">
          <div>AGENTE</div>
          <div>GASTO HOY</div>
          <div>TOPE ACTUAL</div>
          <div>NUEVO TOPE (USD/día)</div>
          <div className="text-right">ACCIÓN</div>
        </div>
        {agents.map((a) => {
          const cap = budgets[a.id] ?? defaultCap;
          const spent = spentFor(a.id);
          const pct = cap > 0 ? Math.min(100, (spent / cap) * 100) : 0;
          const tone = pct > 90 ? "var(--danger)" : pct > 60 ? "var(--warn)" : "var(--good)";
          return (
            <div key={a.id} className="grid grid-cols-[1fr_140px_140px_180px_120px] py-3 px-4 border-b border-[var(--border)] items-center gap-2" data-testid={`bud-${a.id}`}>
              <div>
                <div className="text-sm font-semibold text-white">{a.name}</div>
                <div className="font-mono text-[10px] text-[var(--text-dim)] uppercase">{a.id}</div>
              </div>
              <div>
                <div className="font-mono text-sm text-white">${spent.toFixed(4)}</div>
                <div className="h-1 bg-[var(--bg)] mt-1">
                  <div className="h-1" style={{ width: `${pct}%`, background: tone }} />
                </div>
              </div>
              <div className="font-mono text-sm">${cap.toFixed(2)}</div>
              <Input
                value={drafts[a.id] ?? ""}
                onChange={(e) => setDrafts({ ...drafts, [a.id]: e.target.value })}
                placeholder={`${cap}`}
                className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono h-8"
                data-testid={`bud-inp-${a.id}`}
              />
              <div className="text-right">
                <Button
                  onClick={() => save(a.id)}
                  disabled={drafts[a.id] == null || drafts[a.id] === ""}
                  size="sm"
                  className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80 h-8"
                  data-testid={`bud-save-${a.id}`}
                >
                  Guardar
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="border border-[var(--warn)] bg-[var(--warn)]/5 p-4">
        <div className="label-mono mb-1" style={{ color: "var(--warn)" }}>CÓMO FUNCIONA</div>
        <div className="text-xs text-white leading-relaxed">
          Antes de cada llamada al LLM, el sistema suma el coste estimado de las últimas 24h para ese agente
          y lo compara con el tope. Si se supera, se devuelve HTTP 402 y la acción queda auditada
          con riesgo alto en <b>Auditoría</b>.
        </div>
      </div>
    </div>
  );
}
